/**
 * Account-profile cache — the ONE client source for display name / avatar /
 * hasPassword across the toolbar chip, account panel, settings modal, and
 * profile modal.
 *
 * Loads from GET /api/account after the auth gate resolves a REAL user (the
 * dev single-user fallback has no account row — loadProfile is a no-op there,
 * and every consumer degrades to initials/email). saveProfile PATCHes, merges
 * the cache, and emits 'profile:changed' on the event bus; persistent surfaces
 * (toolbar chip, a mounted settings Account pane) subscribe to that, transient
 * surfaces (the account popover) just read getProfile() fresh at open.
 */

import { eventBus } from './events.js';
import { getAccount, patchAccount, isRealUser } from '../templates/auth-api.js';

let _profile = null;
let _loading = null;
let _activeSub = null;
let _loadGeneration = 0;

/** Sync cache read. Null until loadProfile resolves (or when it degrades). */
export function getProfile() {
  return _profile;
}

/**
 * Fetch (or refresh) the profile for the signed-in real user.
 * Expected-flow fallback: any failure (proxy stub 404s locally, network, a
 * not-yet-deployed worker) leaves the cache null — the UI shows initials and
 * the account email from the auth payload instead. No error surfaces.
 * @param {{force?: boolean, authPayload?: object}} [opts]
 * @returns {Promise<object|null>}
 */
export function loadProfile({ force = false, authPayload = null } = {}) {
  if (authPayload && !isRealUser(authPayload)) {
    clearProfile();
    return Promise.resolve(null);
  }

  const requestedSub = authPayload?.sub ? String(authPayload.sub) : _activeSub;
  if (requestedSub && requestedSub !== _activeSub) {
    // Identity changed while the app stayed mounted. Invalidate both the
    // visible cache and any in-flight request for the previous subject.
    _loadGeneration += 1;
    _activeSub = requestedSub;
    _profile = null;
    _loading = null;
  }

  if (_profile && (!requestedSub || _profile.sub === requestedSub) && !force) {
    return Promise.resolve(_profile);
  }
  if (_loading && _loading.sub === requestedSub && !force) return _loading.promise;

  const generation = ++_loadGeneration;
  const promise = (async () => {
    try {
      const data = await getAccount();
      const responseSub = data?.sub ? String(data.sub) : null;
      const isCurrentRequest = generation === _loadGeneration;
      const matchesActiveSubject = !_activeSub || !responseSub || responseSub === _activeSub;
      if (data?.ok && isCurrentRequest && matchesActiveSubject) {
        if (!_activeSub && responseSub) _activeSub = responseSub;
        _profile = {
          sub: responseSub,
          email: data.email || null,
          displayName: data.displayName || null,
          avatar: data.avatar || null,
          hasPassword: Boolean(data.hasPassword),
          googleLinked: Boolean(data.googleLinked),
          createdAt: data.createdAt || null,
          passkeyCount: Number(data.passkeyCount || 0)
        };
        eventBus.emit('profile:changed', _profile);
      }
    } catch (err) {
      // Documented degradation — see the module header.
      console.warn('[ProfileStore] profile load skipped:', err?.message || err);
    }
    if (_loading?.generation === generation) _loading = null;
    return _profile;
  })();
  _loading = { sub: requestedSub, generation, promise };
  return promise;
}

/**
 * PATCH the profile ({displayName?, avatar?}; null clears a field), merge the
 * cache, and notify subscribers.
 * @returns {Promise<object>} the merged profile
 */
export async function saveProfile(patch) {
  const subjectAtStart = _activeSub;
  const data = await patchAccount(patch);
  // A save started by user A must never repaint user B's in-memory profile if
  // a re-authentication switches subjects while the PATCH is in flight.
  if (subjectAtStart && subjectAtStart !== _activeSub) return _profile;
  _profile = {
    ...(_profile || {}),
    displayName: data.displayName ?? null,
    avatar: data.avatar ?? null,
    // SB-5: adopt the value the SERVER settled on, not the one we sent. The
    // watermark is clamped with MAX() server-side, so a late or out-of-order
    // write can legitimately come back higher than the request — assuming our
    // own value would show a stale badge until the next profile load.
    ...(data.sharesSeenAt === undefined ? {} : { sharesSeenAt: data.sharesSeenAt })
  };
  eventBus.emit('profile:changed', _profile);
  return _profile;
}

/** Drop the cache (logout). */
export function clearProfile() {
  _loadGeneration += 1;
  _profile = null;
  _loading = null;
  _activeSub = null;
}

/**
 * Test seam. The profile is module state reached synchronously by the collab
 * connect gate (SB-4a reads `features.collabAllProjects` from it), so a test
 * needs to place one without standing up the whole /api/account round-trip.
 */
export function _setProfileForTesting(profile) {
  _profile = profile;
  _activeSub = profile?.sub ? String(profile.sub) : null;
  _loading = null;
}
